import { AddressDisplay } from '@components/AddressDisplay';
import {
  AutoComplete,
  Props as AutoCompleteProps,
  Shell,
} from '@components/AutoComplete';
import { Label } from '@components/FieldLabel';
import { useViewOnly } from '@components/ViewOnly';
import { CustomerPickerItemFragment } from '@generated/fragments/customerPickerItem';
import { CustomerPickerItemV2Fragment } from '@generated/fragments/customerPickerItemV2';
import {
  AllCustomersForCustomerPickerDocument,
  AllCustomersForCustomerPickerQuery,
  AllCustomersForCustomerPickerQueryVariables,
} from '@generated/queries/allCustomersForCustomerPicker';
import {
  AllCustomersForCustomerPickerV2Document,
  AllCustomersForCustomerPickerV2Query,
  AllCustomersForCustomerPickerV2QueryVariables,
} from '@generated/queries/allCustomersForCustomerPickerV2';
import { useCustomerV2Flag } from '@hooks/useCustomerV2Flag';
import { useLazyQueryWithDataPromise } from '@hooks/useLazyQueryWithDataPromise';
import { useTamedInputValue } from '@hooks/useTamedInputValue';
import { getNodesFromConnection } from '@utils/graphqlUtils';
import { pipeSeparator } from '@utils/htmlEntities';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';

export type Node = CustomerPickerItemFragment | CustomerPickerItemV2Fragment;

export const CustomerPickerItemDisplay: FC<{
  customer: Maybe<Node>;
  compact?: boolean;
}> = ({ customer, compact }) => (
  <div>
    <strong>{customer?.name}</strong>
    {compact ? <br /> : ' | '}
    <span>
      {customer?.code}
      {pipeSeparator}
      <AddressDisplay value={customer?.mainAddress} city state />
      {pipeSeparator}
      {customer?.customerStatusType?.name}
    </span>
  </div>
);

interface Props extends Omit<AutoCompleteProps<Node>, 'items'> {
  disableSelectOnTab?: boolean;
  name?: string;
  value?: string;
  initialSelectedItem?: Shell<Node>;
  inputProps?: AutoCompleteProps<unknown>['inputProps'];
  /** Input id */
  id?: string;
  autoFocus?: boolean; // defaults to 'true'
  /** Initial customer id, if you want the component to do a lookup */
  initialId?: Maybe<string>;
  filterItems?: (items: Shell<Node>[]) => Shell<Node>[];
}

const toShell = (value: Node): Shell<Node> => ({
  value,
  label: (): ReactNode => <CustomerPickerItemDisplay customer={value} />,
  id: value.id,
});

export const useCustomerSearch = (): ((
  text: string,
  useCustomerV2: boolean,
  kwargs?:
    | AllCustomersForCustomerPickerQueryVariables
    | AllCustomersForCustomerPickerV2QueryVariables
) => Promise<
  CustomerPickerItemFragment[] | CustomerPickerItemV2Fragment[]
>) => {
  const callQueryV1 = useLazyQueryWithDataPromise<
    AllCustomersForCustomerPickerQuery,
    AllCustomersForCustomerPickerQueryVariables
  >(AllCustomersForCustomerPickerDocument);

  const callQueryV2 = useLazyQueryWithDataPromise<
    AllCustomersForCustomerPickerV2Query,
    AllCustomersForCustomerPickerV2QueryVariables
  >(AllCustomersForCustomerPickerV2Document);

  return useCallback(
    async (
      text: string,
      useCustomerV2: boolean,
      kwargs?: AllCustomersForCustomerPickerQueryVariables
    ): Promise<
      CustomerPickerItemFragment[] | CustomerPickerItemV2Fragment[]
    > => {
      if (useCustomerV2) {
        const rawRes = await callQueryV2({
          fetchPolicy: 'network-only',
          variables: { first: 20, filter: { text }, ...kwargs },
        });
        return getNodesFromConnection(rawRes.data.allCustomersV2);
      } else {
        const rawRes = await callQueryV1({
          fetchPolicy: 'network-only',
          variables: { first: 20, filter: { text }, ...kwargs },
        });
        return getNodesFromConnection(rawRes.data.allCustomersNullable);
      }
    },
    [callQueryV1, callQueryV2]
  );
};

export const CustomerPicker: FC<Props> = ({
  disableSelectOnTab,
  name,
  initialSelectedItem: initialSelectedItemProp,
  inputProps,
  id,
  filterItems,
  initialId,
  autoFocus = true,
  ...rest
}) => {
  const { isViewOnly } = useViewOnly();
  const useCustomerV2 = useCustomerV2Flag();

  const [text, setText] = useTamedInputValue({
    initialValue: '',
    debounceTimeout: 200,
    lengthThreshold: 0,
  });

  const [shouldUseInitialCustomer, setShouldUseInitialCustomer] = useState(
    Boolean(initialId)
  );

  const searchCustomers = useCustomerSearch();

  const [dropdownKey, setDropdownKey] = useState(Date.now());

  const [results, setResults] = useState<Node[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect((): void => {
    (async (): Promise<void> => {
      if (text) {
        setLoading(true);
        const arr = await searchCustomers(text, useCustomerV2);
        setResults(arr);
        setLoading(false);
      } else if (initialId && shouldUseInitialCustomer) {
        setLoading(true);
        const arr = await searchCustomers(text, useCustomerV2, {
          filter: { id: initialId },
          first: 20,
        });
        setResults(arr);
        setLoading(false);
      } else if (!text) {
        setLoading(false);
        setResults([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, shouldUseInitialCustomer]);

  const items = results?.map(toShell) || [];

  let gotCustomerFromInitialId = false;

  let initialSelectedItem = initialSelectedItemProp;
  if (initialId && !initialSelectedItem) {
    initialSelectedItem = results
      .filter((obj) => obj.id === initialId)
      .map(toShell)[0];
    if (initialSelectedItem) {
      gotCustomerFromInitialId = true;
    }
  }

  useEffect(() => {
    if (shouldUseInitialCustomer) {
      setDropdownKey(Date.now());
    }
  }, [gotCustomerFromInitialId, shouldUseInitialCustomer]);

  return isViewOnly ? (
    <div>
      {rest.label && <Label css={{ marginBottom: '6px' }}>{rest.label}</Label>}
      <div data-testid={name || 'customer-search'}>
        {rest.selectedItem?.label}
      </div>
    </div>
  ) : (
    <AutoComplete<Node>
      key={dropdownKey}
      onInputValueChange={(e): void => {
        setShouldUseInitialCustomer(false);
        setText(e);
      }}
      loading={loading}
      items={filterItems ? filterItems(items) : items}
      data-testid="customer-search"
      inputProps={{
        placeholder: 'Search Customers',
        ['data-testid']: 'customer-search-input',
        name,
        id: id || name,
        autoFocus,
        ...inputProps,
      }}
      initialSelectedItem={initialSelectedItem}
      disableSelectOnTab={disableSelectOnTab}
      {...rest}
    />
  );
};
